import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useMemo } from 'react';
import { starterRecipeService, StarterRecipe } from '@/services/starterRecipe';
import { useUser } from '@/contexts/UserContext';
import { usePersonalRecipes } from '@/hooks/usePersonalRecipes';
import { toast } from 'sonner';

const STARTER_RECIPES_KEY = ['starter-recipes', 'active'];

export const useStarterRecipes = () => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [sessionAddedIds, setSessionAddedIds] = useState<Set<string>>(new Set());
  
  // Fetch user's personal recipes to check which starter recipes are already added
  const { data: personalRecipes = [] } = usePersonalRecipes();

  const { data: starterRecipes = [], isLoading } = useQuery({
    queryKey: STARTER_RECIPES_KEY,
    queryFn: starterRecipeService.getActiveStarterRecipes,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Compute which starter recipe IDs are already in user's collection
  const existingStarterRecipeIds = useMemo(() => {
    const ids = new Set<string>();
    personalRecipes.forEach(recipe => {
      // Check if recipe was copied from a starter recipe
      if (recipe.suggestion_id?.startsWith('starter:')) {
        const starterId = recipe.suggestion_id.replace('starter:', '');
        ids.add(starterId);
      }
    });
    return ids;
  }, [personalRecipes]);

  // Combine existing recipes with session-added ones
  const addedRecipeIds = useMemo(() => {
    return new Set([...existingStarterRecipeIds, ...sessionAddedIds]);
  }, [existingStarterRecipeIds, sessionAddedIds]);

  const addMutation = useMutation({
    mutationFn: async (starterRecipe: StarterRecipe) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      return starterRecipeService.copyToCollection(starterRecipe, user.id);
    },
    onSuccess: (_, starterRecipe) => {
      setSessionAddedIds(prev => new Set([...prev, starterRecipe.id]));
      toast.success(`"${starterRecipe.title}" added to your collection!`);
      // Invalidate personal recipes to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['personal-recipes'] });
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
    },
    onError: (error, starterRecipe) => {
      console.error('Failed to add recipe:', error);
      toast.error(`Failed to add "${starterRecipe.title}". Please try again.`);
    }
  });

  const addToCollection = async (starterRecipe: StarterRecipe) => {
    await addMutation.mutateAsync(starterRecipe);
  };

  return {
    starterRecipes,
    isLoading,
    addToCollection,
    addedRecipeIds,
    isAdding: addMutation.isPending,
    addingRecipeId: addMutation.variables?.id
  };
};
